import PropTypes from 'prop-types'
import React from 'react'
import { NextSeo } from 'next-seo'
import ProfilePage from '@components/coaches-page/coaches-page'
import defaults from '@config/defaults'
import { JsonLd } from '@components/atoms'
import useFeatureFlags from '@lib/feature-flags/useFeatureFlags'
import { flags } from '@lib/feature-flags/constants'
import {
  PERSONA_PROFILE_TAB_LABELS,
  isCoachProfilePersonaTabId,
  tabIdFromSection,
} from '@components/coach-bio/coach-profile-tab-utils'
import { coachProfileJsonLdBookingUrl } from '@lib/pga-coach/coach-scheduling'

const SECTION_LABELS = {
  meet: null, // canonical About — no section suffix
  contact: 'Contact',
  ...PERSONA_PROFILE_TAB_LABELS,
}

const CoachPage = (props) => {
  const {
    coach: profile,
    section,
    initialOfferingsByPersona,
    coachProfileUxEnabled: coachProfileUxEnabledProp,
  } = props
  const [coachProfileUxFromContext] = useFeatureFlags([flags.FLAG_COACH_PROFILE_UX])
  const coachProfileUxEnabled = coachProfileUxEnabledProp ?? coachProfileUxFromContext
  const activeSection = coachProfileUxEnabled ? section : 'about'

  const coachName = profile.displayName || `${profile.firstName} ${profile.lastName}`
  const coachCityState = `${profile.city}, ${profile.state}`

  const tabId = tabIdFromSection(activeSection)
  const sectionLabel = coachProfileUxEnabled ? SECTION_LABELS[tabId] : null
  const isPersona = coachProfileUxEnabled && isCoachProfilePersonaTabId(tabId)
  const isContact = coachProfileUxEnabled && tabId === 'contact'

  const baseCoachUrl = `${defaults.baseUrl}coach/${profile.customSlug}`
  const coachUrl = activeSection && activeSection !== 'about'
    ? `${baseCoachUrl}/${activeSection}`
    : baseCoachUrl

  const seoTitle = sectionLabel
    ? `${coachName} \u2014 ${sectionLabel} | Golf Lessons | ${coachCityState}`
    : `${coachName} | Golf Lessons | ${coachCityState}`

  let seoDescription
  if (isPersona) {
    seoDescription = `${sectionLabel} programs and golf lessons offered by ${coachName}, a PGA Golf Professional in ${coachCityState}.`
  } else if (isContact) {
    seoDescription = `Contact ${coachName}, a PGA Golf Professional in ${coachCityState}, about lessons, programs, and golf coaching.`
  } else {
    seoDescription = `${coachName} is a PGA Golf Professional who offers customized Golf Lessons and will work with you to create a winning strategy for your golf game.`
  }

  const bookingUrl = coachProfileJsonLdBookingUrl(profile)

  const shouldIndexForSEO = profile.status === 'ACTIVE' && profile.coach.canPromoteContent
  const showContactForm = profile.coach.preferences?.acceptsLeads
    && !profile.coach.freeTierLimitsReached?.includes('LEADS')

  return (
    <>
      <NextSeo
        noindex={!shouldIndexForSEO || defaults.noindexNofollow}
        nofollow={!shouldIndexForSEO || defaults.noindexNofollow}
        title={seoTitle}
        description={seoDescription}
        canonical={coachUrl}
        openGraph={{
          type: 'website',
          title: seoTitle,
          url: coachUrl,
          images: profile.profilePhoto && [{ url: profile.profilePhoto }],
        }}
      />
      <ProfilePage
        coach={profile}
        section={activeSection}
        initialOfferingsByPersona={initialOfferingsByPersona}
      />
      <JsonLd {...{
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': 'https://www.pga.com/',
              name: 'PGA Of America',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': 'https://www.pga.com/coach',
              name: 'Golf Lessons',
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@id': `https://www.pga.com/coaches/${encodeURIComponent(profile.state)}/${encodeURIComponent(profile.city)}`,
              name: `${coachCityState}`,
            },
          },
          {
            '@type': 'ListItem',
            position: 4,
            item: {
              '@id': baseCoachUrl,
              name: coachName,
            },
          },
          sectionLabel && {
            '@type': 'ListItem',
            position: 5,
            item: {
              '@id': coachUrl,
              name: sectionLabel,
            },
          },
        ].filter(Boolean),
      }}
      />
      <JsonLd {...{
        '@type': 'ItemList',
        name: 'Coach Profile Navigation',
        itemListElement: [
          showContactForm && coachProfileUxEnabled && {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'SiteNavigationElement',
              name: 'Send a Request',
              url: `${baseCoachUrl}#coach-profile-contact`,
              about: 'Scrolls to the contact form on the coach profile page',
            },
          },
          bookingUrl && {
            '@type': 'ListItem',
            position: showContactForm && coachProfileUxEnabled ? 2 : 1,
            item: {
              '@type': 'SiteNavigationElement',
              name: 'Book Now',
              url: bookingUrl,
              about: 'Opens the booking page in a new tab',
            },
          },
        ].filter(Boolean),
      }}
      />
      <JsonLd {...{
        '@type': 'ProfessionalService',
        name: coachName,
        url: coachUrl,
        image: profile.profilePhoto || 'https://www.pga.com/images/person_icon.png',
        address: {
          '@type': 'PostalAddress',
          addressLocality: profile.city,
          addressRegion: profile.state,
          postalCode: profile.zipCode,
          addressCountry: 'United States',
        },
      }}
      />
    </>
  )
}

CoachPage.propTypes = {
  coach: PropTypes.object,
  /** Active section slug (`about`, `contact`, persona id). Defaults to About. */
  section: PropTypes.string,
  /** SSR-prefetched offering map for the About shop. */
  initialOfferingsByPersona: PropTypes.objectOf(PropTypes.array),
  /** When false, section tabs/SEO fall back to the canonical About surface. */
  coachProfileUxEnabled: PropTypes.bool,
}

export default CoachPage
